import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Category } from '../../types';
import { adminAPI } from '../../services/api';
import SearchableSelect from './SearchableSelect';
import './AdminFormStyles.css';

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  category?: Category | null;
  defaultParentId?: number | null;
  categories: Category[];
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  category,
  defaultParentId = null,
  categories
}) => {
  const [formData, setFormData] = useState({
    name_bn: '',
    name_en: '',
    slug: '',
    parent: '',
    is_active: true,
    display_order: 0,
    image: null as File | null
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [parentSearch, setParentSearch] = useState('');
  const [parentOptions, setParentOptions] = useState<Category[]>(categories);

  useEffect(() => {
    if (category) {
      setFormData({
        name_bn: category.name_bn || '',
        name_en: category.name_en || '',
        slug: category.slug || '',
        parent: (category as any).parent || '',
        is_active: category.is_active ?? true,
        display_order: category.display_order || 0,
        image: null
      });
    } else {
      setFormData({
        name_bn: '',
        name_en: '',
        slug: '',
        parent: defaultParentId ? String(defaultParentId) : '',
        is_active: true,
        display_order: 0,
        image: null
      });
    }
    setErrors({});
  }, [category, defaultParentId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(async () => {
      try {
        const response = await adminAPI.getCategories({ search: parentSearch, page_size: 50, ordering: 'name_en' });
        setParentOptions(Array.isArray(response.data) ? response.data : (response.data?.results || []));
      } catch { setParentOptions(categories); }
    }, 300);
    return () => clearTimeout(timer);
  }, [parentSearch, categories, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const files = (e.target as HTMLInputElement).files;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? (files?.[0] || null) : type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const generateSlug = () => {
    const slug = formData.name_en
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.name_bn.trim()) newErrors.name_bn = 'Bangla name is required';
    if (!formData.name_en.trim()) newErrors.name_en = 'English name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      let submitData: any;
      if (formData.image) {
        submitData = new FormData();
        submitData.append('name_bn', formData.name_bn);
        submitData.append('name_en', formData.name_en);
        submitData.append('slug', formData.slug);
        if (formData.parent) submitData.append('parent', String(formData.parent));
        submitData.append('is_active', String(formData.is_active));
        submitData.append('display_order', String(formData.display_order));
        submitData.append('image', formData.image);
      } else {
        submitData = {
          name_bn: formData.name_bn,
          name_en: formData.name_en,
          slug: formData.slug,
          parent: formData.parent ? Number(formData.parent) : null,
          is_active: formData.is_active,
          display_order: Number(formData.display_order)
        };
      }
      await onSave(submitData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const descendantIds = new Set<number>();
  if (category) {
    const collect = (parentId: number) => {
      categories
        .filter((cat: any) => cat.parent === parentId)
        .forEach((child: Category) => {
          descendantIds.add(child.id);
          collect(child.id);
        });
    };
    collect(category.id);
  }
  
  const availableParents = parentOptions.filter((cat: Category) => 
    !category || (cat.id !== category.id && !descendantIds.has(cat.id))
  );
  const defaultParent = defaultParentId ? categories.find(cat => cat.id === defaultParentId) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? 'Edit Category' : defaultParent ? `Add Subcategory: ${defaultParent.name_en}` : 'Add Root Category'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Name Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (Bangla) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_bn"
                value={formData.name_bn}
                onChange={handleChange}
                placeholder="ক্যাটাগরির নাম লিখুন"
                className={errors.name_bn ? 'has-error' : ''}
              />
              {errors.name_bn && <span className="error-text">{errors.name_bn}</span>}
            </div>
          </div>

          {/* Name English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (English) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                onBlur={generateSlug}
                placeholder="Enter category name"
                className={errors.name_en ? 'has-error' : ''}
              />
              {errors.name_en && <span className="error-text">{errors.name_en}</span>}
            </div>
          </div>

          {/* Slug */}
          <div className="admin-form-row">
            <div className="admin-form-label">Slug *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="category-slug"
                className={errors.slug ? 'has-error' : ''}
              />
              {errors.slug && <span className="error-text">{errors.slug}</span>}
              <span className="admin-form-hint">Auto-generated from English name</span>
            </div>
          </div>

          {/* Parent Category */}
          <div className="admin-form-row">
            <div className="admin-form-label">Parent Category</div>
            <div className="admin-form-input">
              <SearchableSelect value={formData.parent} onChange={parent=>setFormData(prev=>({...prev,parent}))} onSearch={setParentSearch} placeholder="None / type to search parents…" options={availableParents.map(cat=>({value:cat.id,label:cat.name_en}))}/>
            </div>
          </div>

          {/* Display Order */}
          <div className="admin-form-row">
            <div className="admin-form-label">Display Order</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          {/* Image */}
          <div className="admin-form-row">
            <div className="admin-form-label">Image / Icon</div>
            <div className="admin-form-input">
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleChange}
              />
              {category?.image && <span className="admin-form-hint">Leave empty to keep the current image</span>}
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Category</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryFormModal;
